import React, { useEffect, useState } from "react";
import FormInput from "components/Inputs/FormInput";
import MarkdownEditor from "components/MardownEditor/MarkdownEditor";
import CategoriesSelect from "components/CustomSelects/CategoriesSelect";
import useAccount from "classes/Accounts/hooks/useAccount";
import useTemplate from "hooks/useTemplate";
import { useMediaQuery } from "react-responsive";
import clsx from "clsx";
import AITextGenerator from "Pages/Posts/components/AIGeneration/AITextGenerator";
import AIContentGenerator from "Pages/Posts/components/AIGeneration/AIContentGenerator";
import Button from "components/Button/Button";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

export default function PostContentStep(props) {
	let post = props.object;
	const { t } = useTranslation();

	const { account, isAdmin } = useAccount();
	const [titlePost, setTitlePost] = useState(post.title() || '');
	const [textPost, setTextPost] = useState(post.txt() || '');
	const [isAiTextGeneratorOpen, setIsAiTextGeneratorOpen] = useState(false);
	const [isAiContentGeneratorOpen, setIsAiContentGeneratorOpen] = useState(false);
	const [isTitleGenerating, setIsTitleGenerating] = useState(false);
	const [isContentGenerating, setIsContentGenerating] = useState(false);

	const { getAppFunction } = useTemplate();

	const isMobile = useMediaQuery({ query: "(max-width: 700px)" });

	const closeAllModals = () => {
		setIsAiTextGeneratorOpen(false);
		setIsAiContentGeneratorOpen(false);
	};

	function catsSelect(values) {
		post.categories(
			values.map((a) => ({
				...a,
				CategoryId: (!a.isNew ? a.value : false),
				Name: a.label, isNew: a.isNew ? true : false,
			}))
		);
		props.refreshPreview();
	}

	const extractImagesFromMarkdown = (markdownContent) => {
		const imageRegex = /!\[.*?\]\((data:image\/[^;]+;base64,[^)]+)\)/g;
		const images = [];
		let match;
		let index = 0;

		while ((match = imageRegex.exec(markdownContent)) !== null) {
			images.push({
				DocumentFile: match[1], // La data URL complète
				NewIndex: index++,
				originalMatch: match[0] // Pour remplacer plus tard
			});
		}

		return images;
	}
	const setTitleGenerated = (title) => {
		setTitlePost(title);
		setIsTitleGenerating(false);
	}

	const setTextGenerated = (text) => {
		setTextPost(text);
		setIsContentGenerating(false);
	}

	const startTitleGeneration = () => {
		setIsTitleGenerating(true);
	}

	const startContentGeneration = () => {
		setIsContentGenerating(true);
	}

	useEffect(() => {
		post.title(titlePost);
		props.refreshPreview();
	}, [titlePost]);

	useEffect(() => {
		post.txt(textPost);
		props.refreshPreview();
	}, [textPost]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (event.target.closest('.icon-btn')) {
				return;
			}
			const modals = document.querySelectorAll('.modal-ia');
			let clickedOutside = true;

			modals.forEach(modal => {
				if (modal.contains(event.target)) {
					clickedOutside = false;
				}
			});

			if (clickedOutside) {
				closeAllModals();
			}
		};

		if (isAiTextGeneratorOpen || isAiContentGeneratorOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isAiTextGeneratorOpen, isAiContentGeneratorOpen]);

	return (
		<div className="inner-step-cont">
			{
				post.moderationComment() && (
					<div className="errors-cont m-2">
						<div className="font-bold">{t("Posts.MODERATION_MSG")}:</div>
						<div>{post.moderationComment()}</div>
					</div>
				)
			}
			{
				post.postTypeId() === 3 &&
				<FormInput
					onChange={(val) => {
						post.subTitle(val);
						props.refreshPreview();
					}}
					className="w-100"
					title={t("Posts.POST_SUBTITLE")}
					value={post.subTitle()}
				/>}
			<div className="d-flex">
				<FormInput
					autoFocus={true}
					onChange={(val) => {
						post.title(val);
						props.refreshPreview();
					}}
					className="w-100"
					title={t("Posts.POST_TITLE")}
					value={isTitleGenerating ? "" : titlePost}
					placeholder={isTitleGenerating ? t('AI.GeneratingTitle') : ""}
					disabled={isTitleGenerating}
				/>
				<Button className={`icon-btn ${isAiTextGeneratorOpen ? "ai-modal-open" : ""}`} disabled={post.title() === "" || isTitleGenerating} onClick={() => {
					setIsAiTextGeneratorOpen(!isAiTextGeneratorOpen);
					if (isAiContentGeneratorOpen) {
						setIsAiContentGeneratorOpen(false);
					}
				}}>AI</Button>
			</div>
			{isAiTextGeneratorOpen && <AITextGenerator className="modal-ia" title={post.title()} setTitleGenerated={setTitleGenerated} onStartGeneration={startTitleGeneration} />}
			{
				props.categories !== false &&
				<CategoriesSelect
					noTitle={false}
					onSelect={catsSelect}
					initialValue={post.categories ? post.categories().map(a => ({ label: a.Name, value: a.CategoryId, ...a })) : false}
					type={"post-" + post.postTypeId()}
					companyId={account.CompanyId}
					className="mt-2"
				/>
			}
			<div className={clsx("mt-2 pos-relative", isMobile && "mobile")}>
				<div className="title-form">
					{t("Posts.POST_TEXT")}
				</div>
				<MarkdownEditor
					initialValue={post.txt()}
					toolbar={post.postTypeId() !== 13 && (getAppFunction("PostsPage.CanUseMarkdown") || isAdmin())}
					value={isContentGenerating ? t('AI.GeneratingContent') : textPost}
					onChange={(val) => {
						post.txt(val);
						const images = extractImagesFromMarkdown(val);
						post.pendingImages(images);
						props.refreshPreview();
						if (!isContentGenerating) {
							post.txt(val);
							props.refreshPreview();
						}
					}}
					disabled={isContentGenerating}
				/>
				<Button className={`icon-btn markdow-btn-ia ${isAiContentGeneratorOpen ? "ai-modal-open" : ""}`} disabled={post.txt() === "" || isContentGenerating} onClick={() => {
					setIsAiContentGeneratorOpen(!isAiContentGeneratorOpen);
					if (isAiTextGeneratorOpen) {
						setIsAiTextGeneratorOpen(false);
					}
				}}>AI</Button>
				{isAiContentGeneratorOpen && <AIContentGenerator className="modal-ia markdown" text={post.txt()} setTextGenerated={setTextGenerated} onStartGeneration={startContentGeneration} />}
			</div>


		</div>
	);
}

export function checkPostContent(post) {
	let errors = [];

	if (post.postTypeId() === 3) {
		if (!post.subTitle()?.length)
			errors.push(t("Posts.SUBTITLE_ERROR"));
	}
	if (!post.title()?.length)
		errors.push(t("Posts.TITLE_ERROR"));
	let text = post.txt();
	if (!text?.length)
		errors.push(t("Posts.TEXT_ERROR"));
	return (errors.length ? errors : true);
}
